import { useQuery } from 'convex/react'
import { api } from '../../../../convex/_generated/api'
import { useAuth } from '@/contexts/auth-context'
import { cn } from '@/lib/utils'
import { useState, useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  Newspaper,
  Bug,
  Wrench,
} from 'lucide-react'

type FeedItem = {
  id: string
  timestamp: number
  scope: 'issue' | 'experiment'
  kind: string
  title: string
  summary: string
  detail?: string
  issueNumber?: number
  flagKey?: string
  castSession?: string
  actor?: string
  severity?: string
  metadata?: any
  clusterKey?: string
}

type FeedCluster = {
  key: string
  scope: 'issue' | 'experiment'
  items: FeedItem[]
  latestTimestamp: number
}

const SEVERITY_STYLE: Record<string, string> = {
  P0: 'bg-red-100 text-red-800',
  P1: 'bg-orange-100 text-orange-800',
  P2: 'bg-yellow-100 text-yellow-800',
}

function formatAge(ts: number): string {
  const diff = Date.now() - ts
  if (diff < 60000) return 'just now'
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`
  return `${Math.floor(diff / 86400000)}d ago`
}

// Each lifecycle event maps to the specialized agent that produced it, so the feed
// reads as distinct personas (Verifier, Fixer, Monitor, Strategist…) instead of one
// anonymous "Optimizer". Both pipelines run inside the daemon but do separate jobs.
type AgentIdentity = { name: string; color: string }

const KIND_AGENT: Record<string, AgentIdentity> = {
  // Issue pipeline
  issue_verified: { name: 'Verifier', color: 'bg-blue-600' },
  issue_false_positive: { name: 'Verifier', color: 'bg-blue-600' },
  issue_inconclusive: { name: 'Verifier', color: 'bg-blue-600' },
  issue_fix_deployed: { name: 'Fixer', color: 'bg-orange-600' },
  issue_fix_failed: { name: 'Fixer', color: 'bg-orange-600' },
  issue_confirmed_fixed: { name: 'Monitor', color: 'bg-emerald-600' },
  issue_regressed: { name: 'Monitor', color: 'bg-red-600' },
  // Experiment / optimizer pipeline
  optimizer_target_picked: { name: 'Strategist', color: 'bg-violet-600' },
  optimizer_variants_generated: { name: 'Variant Gen', color: 'bg-fuchsia-600' },
  optimizer_launched: { name: 'Launcher', color: 'bg-indigo-600' },
  optimizer_monitor_check: { name: 'Monitor', color: 'bg-emerald-600' },
  optimizer_killed_variant: { name: 'Shipper', color: 'bg-amber-600' },
  optimizer_shipped: { name: 'Shipper', color: 'bg-amber-600' },
  optimizer_cleanup: { name: 'Polisher', color: 'bg-teal-600' },
  optimizer_aborted: { name: 'Strategist', color: 'bg-violet-600' },
}

function agentFor(item: Pick<FeedItem, 'kind' | 'scope'>): AgentIdentity {
  const byKind = KIND_AGENT[item.kind]
  if (byKind) return byKind
  if (item.scope === 'experiment') return { name: 'Optimizer', color: 'bg-indigo-600' }
  return { name: 'Daemon', color: 'bg-neutral-600' }
}

function agentName(item: FeedItem): string {
  return agentFor(item).name
}

function agentColor(item: FeedItem): string {
  return agentFor(item).color
}

function PostMeta({ item }: { item: FeedItem }) {
  return (
    <div className="flex items-center gap-2 text-[11px] text-neutral-400">
      <span>{formatAge(item.timestamp)}</span>
      {item.issueNumber != null && (
        <a href={`/admin/issues/${item.issueNumber}`}
          className="text-blue-500 hover:text-blue-700 hover:underline font-mono"
          onClick={(e) => e.stopPropagation()}>
          ISS-{item.issueNumber}
        </a>
      )}
      {item.flagKey && (
        <a href={`/admin/flags?q=${item.flagKey}`}
          className="text-indigo-400 hover:text-indigo-600 hover:underline font-mono text-[10px]"
          onClick={(e) => e.stopPropagation()}>
          {item.flagKey}
        </a>
      )}
      {item.castSession && item.castSession !== 'unknown' && (
        <a href={item.castSession} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-0.5 text-violet-400 hover:text-violet-600 hover:underline"
          onClick={(e) => e.stopPropagation()}>
          session <ExternalLink className="h-2.5 w-2.5" />
        </a>
      )}
      {item.severity && (
        <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', SEVERITY_STYLE[item.severity] ?? '')}>
          {item.severity}
        </span>
      )}
    </div>
  )
}

function MarkdownContent({ content, className }: { content: string; className?: string }) {
  return (
    <div className={cn(
      'prose prose-sm prose-neutral max-w-none text-[13px] leading-relaxed',
      'prose-headings:text-[13px] prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1',
      'prose-p:my-1.5 prose-li:my-0.5',
      'prose-code:text-[12px] prose-code:bg-neutral-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded',
      'prose-pre:bg-neutral-900 prose-pre:text-neutral-100 prose-pre:text-[11px] prose-pre:rounded-md',
      'prose-table:text-[11px] prose-th:px-2 prose-th:py-1 prose-td:px-2 prose-td:py-1',
      'prose-th:bg-neutral-100 prose-th:font-medium prose-table:my-2',
      'prose-strong:font-semibold',
      className,
    )}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  )
}

// A single post by an agent — the core feed unit
function PostCard({ item, defaultExpanded }: { item: FeedItem; defaultExpanded?: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? false)
  const hasDetail = !!(item.detail && item.detail.length > 0)

  return (
    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
      <div className="px-5 py-4">
        {/* Author line */}
        <div className="flex items-center gap-2.5 mb-3">
          <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold', agentColor(item))}>
            {agentName(item).charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-neutral-900">{agentName(item)}</span>
              <span className="text-[11px] text-neutral-400">{formatAge(item.timestamp)}</span>
            </div>
            <PostMeta item={item} />
          </div>
        </div>

        {/* Post body */}
        {hasDetail ? (
          <>
            {expanded ? (
              <div className="cursor-pointer" onClick={() => setExpanded(false)}>
                <MarkdownContent content={item.detail!} />
              </div>
            ) : (
              <div className="cursor-pointer" onClick={() => setExpanded(true)}>
                <p className="text-sm text-neutral-700 leading-relaxed">{item.summary}</p>
                <span className="text-[11px] text-indigo-500 mt-1.5 inline-flex items-center gap-0.5">
                  Read more <ChevronRight className="h-3 w-3" />
                </span>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-neutral-700 leading-relaxed">{item.summary}</p>
        )}
      </div>

      {/* Eval result if present */}
      {item.metadata?.evalResult && (
        <div className="px-5 pb-3">
          <span className={cn(
            'text-[11px] font-bold px-1.5 py-0.5 rounded',
            item.metadata.evalResult.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800',
          )}>
            {item.metadata.evalResult.passed ? 'PASS' : 'FAIL'}
            {item.metadata.evalResult.overallScore != null &&
              ` ${(item.metadata.evalResult.overallScore * 100).toFixed(0)}%`}
          </span>
        </div>
      )}
    </div>
  )
}

// Experiment cluster — shows rich posts, hides lifecycle noise
function ExperimentClusterCard({ cluster }: { cluster: FeedCluster }) {
  const [showOlder, setShowOlder] = useState(false)
  const [showTimeline, setShowTimeline] = useState(false)

  const flagKey = cluster.items[0]?.flagKey
  const posts = cluster.items.filter(i => i.detail && i.detail.length > 0)
  const events = cluster.items.filter(i => !i.detail || i.detail.length === 0)

  const latestPost = posts[0]
  const olderPosts = posts.slice(1)

  // If no posts at all, show a minimal card with the best available summary
  if (!latestPost) {
    const bestItem = cluster.items[0]
    const agent = agentFor(bestItem)
    return (
      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        <div className="px-5 py-4">
          <div className="flex items-center gap-2.5 mb-3">
            <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold', agent.color)}>
              {agent.name.charAt(0)}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-neutral-900">{agent.name}</span>
                <span className="text-[11px] text-neutral-400">{formatAge(cluster.latestTimestamp)}</span>
              </div>
              {flagKey && (
                <a href={`/admin/flags?q=${flagKey}`}
                  className="text-[10px] text-indigo-400 hover:text-indigo-600 hover:underline font-mono">
                  {flagKey}
                </a>
              )}
            </div>
          </div>
          <p className="text-sm text-neutral-700 leading-relaxed">
            {bestItem?.summary || `${cluster.items.length} lifecycle events — awaiting first report`}
          </p>
          {events.length > 0 && (
            <button
              onClick={() => setShowTimeline(!showTimeline)}
              className="text-[11px] text-neutral-400 hover:text-neutral-600 mt-2 flex items-center gap-0.5"
            >
              {showTimeline ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {events.length} events
            </button>
          )}
          {showTimeline && <EventTimeline events={events} />}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
      <div className="px-5 py-4">
        {/* Author line */}
        <div className="flex items-center gap-2.5 mb-3">
          <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-white text-[11px] font-bold', agentColor(latestPost))}>
            {agentName(latestPost).charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-neutral-900">{agentName(latestPost)}</span>
              <span className="text-[11px] text-neutral-400">{formatAge(latestPost.timestamp)}</span>
            </div>
            <PostMeta item={latestPost} />
          </div>
        </div>

        {/* Latest post content — shown inline */}
        <MarkdownContent content={latestPost.detail!} />
      </div>

      {/* Older posts */}
      {olderPosts.length > 0 && (
        <div className="border-t border-neutral-100">
          <button
            onClick={() => setShowOlder(!showOlder)}
            className="w-full px-5 py-2 text-[12px] text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50 flex items-center gap-1 transition-colors"
          >
            {showOlder ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {olderPosts.length} earlier {olderPosts.length === 1 ? 'post' : 'posts'}
          </button>
          {showOlder && olderPosts.map((post) => (
            <OlderPost key={post.id} item={post} />
          ))}
        </div>
      )}

      {/* Lifecycle events — tiny footer */}
      {events.length > 0 && (
        <div className="border-t border-neutral-100">
          <button
            onClick={() => setShowTimeline(!showTimeline)}
            className="w-full px-5 py-1.5 text-[11px] text-neutral-400 hover:text-neutral-500 hover:bg-neutral-50/50 flex items-center gap-1 transition-colors"
          >
            {showTimeline ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
            {events.length} lifecycle events
          </button>
          {showTimeline && <EventTimeline events={events} />}
        </div>
      )}
    </div>
  )
}

function OlderPost({ item }: { item: FeedItem }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="px-5 py-3 border-t border-neutral-100">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[12px] font-medium text-neutral-600">{agentName(item)}</span>
        <span className="text-[11px] text-neutral-400">{formatAge(item.timestamp)}</span>
      </div>
      {expanded ? (
        <div className="cursor-pointer" onClick={() => setExpanded(false)}>
          <MarkdownContent content={item.detail!} />
        </div>
      ) : (
        <div className="cursor-pointer" onClick={() => setExpanded(true)}>
          <p className="text-[13px] text-neutral-600 leading-relaxed line-clamp-2">{item.summary}</p>
          <span className="text-[11px] text-indigo-500 mt-1 inline-flex items-center gap-0.5">
            Read more <ChevronRight className="h-3 w-3" />
          </span>
        </div>
      )}
    </div>
  )
}

function EventTimeline({ events }: { events: FeedItem[] }) {
  return (
    <div className="px-5 pb-2">
      {events.map((ev) => (
        <div key={ev.id} className="flex items-center gap-2 py-1 text-[11px] text-neutral-400">
          <div className="w-1.5 h-1.5 rounded-full bg-neutral-300 shrink-0" />
          <span className="truncate">{ev.summary || ev.title}</span>
          <span className="shrink-0 text-[10px]">{formatAge(ev.timestamp)}</span>
        </div>
      ))}
    </div>
  )
}

// Issue cluster — compact summary with expandable details
function IssueClusterCard({ cluster }: { cluster: FeedCluster }) {
  const [expanded, setExpanded] = useState(false)

  const count = (k: string) => cluster.items.filter(i => i.kind === k).length
  const deployedCount = count('issue_fix_deployed')
  const verifiedCount = count('issue_verified')
  const confirmedCount = count('issue_confirmed_fixed')
  const regressedCount = count('issue_regressed')
  const fpCount = count('issue_false_positive')
  const failedCount = count('issue_fix_failed')
  const inconclusiveCount = count('issue_inconclusive')

  const parts: string[] = []
  if (deployedCount > 0) parts.push(`${deployedCount} fixed`)
  if (verifiedCount > 0) parts.push(`${verifiedCount} verified`)
  if (confirmedCount > 0) parts.push(`${confirmedCount} confirmed`)
  if (regressedCount > 0) parts.push(`${regressedCount} regressed`)
  if (fpCount > 0) parts.push(`${fpCount} dismissed`)
  if (failedCount > 0) parts.push(`${failedCount} stalled`)
  if (inconclusiveCount > 0) parts.push(`${inconclusiveCount} inconclusive`)

  // One badge per issue showing its most recent state (items are sorted newest-first).
  const latestByIssue = new Map<number, FeedItem>()
  for (const item of cluster.items) {
    if (item.issueNumber == null || latestByIssue.has(item.issueNumber)) continue
    latestByIssue.set(item.issueNumber, item)
  }
  const badgeItems = [...latestByIssue.values()]
  const distinctIssues = badgeItems.length

  const hasDeployed = deployedCount > 0
  let IconComp = Bug
  let iconBg = 'bg-blue-600'
  if (hasDeployed) { IconComp = Wrench; iconBg = 'bg-orange-600' }

  return (
    <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
      <div className="px-5 py-4">
        <div className="flex items-center gap-2.5 mb-3">
          <div className={cn('w-7 h-7 rounded-full flex items-center justify-center text-white', iconBg)}>
            <IconComp className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-neutral-900">Issue Pipeline</span>
              <span className="text-[11px] text-neutral-400">{formatAge(cluster.latestTimestamp)}</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-neutral-700 leading-relaxed">
          {`Worked through ${distinctIssues} issue${distinctIssues === 1 ? '' : 's'} — ${parts.join(', ')}.`}
        </p>

        {/* Issue badges — one per issue, latest state */}
        <div className="flex flex-wrap gap-1.5 mt-3">
          {badgeItems.map((item) => (
            <a
              key={item.id}
              href={item.issueNumber != null ? `/admin/issues/${item.issueNumber}` : undefined}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                'text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors',
                item.kind === 'issue_fix_deployed' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' :
                item.kind === 'issue_confirmed_fixed' ? 'bg-green-100 text-green-700 hover:bg-green-200' :
                item.kind === 'issue_regressed' ? 'bg-red-100 text-red-700 hover:bg-red-200 ring-1 ring-red-300' :
                item.kind === 'issue_fix_failed' ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' :
                item.kind === 'issue_false_positive' ? 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200 line-through' :
                item.severity === 'P0' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                item.severity === 'P1' ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' :
                'bg-blue-50 text-blue-600 hover:bg-blue-100',
              )}
            >
              ISS-{item.issueNumber}
            </a>
          ))}
        </div>

        {/* Expand to see individual issues */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-[11px] text-neutral-400 hover:text-neutral-600 mt-2 flex items-center gap-0.5 transition-colors"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {expanded ? 'collapse' : 'details'}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-neutral-100">
          {cluster.items.map((item) => {
            const hasDetail = !!(item.detail && item.detail.length > 0)
            return (
              <IssueRow key={item.id} item={item} hasDetail={hasDetail} />
            )
          })}
        </div>
      )}
    </div>
  )
}

function IssueRow({ item, hasDetail }: { item: FeedItem; hasDetail: boolean }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="border-b border-neutral-100 last:border-b-0">
      <div
        className={cn('px-5 py-2.5', hasDetail && 'cursor-pointer hover:bg-neutral-50/50')}
        onClick={() => hasDetail && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {item.issueNumber != null && (
            <span className="text-[11px] font-mono text-blue-500 shrink-0">ISS-{item.issueNumber}</span>
          )}
          {item.severity && (
            <span className={cn('text-[9px] font-bold px-1 py-0.5 rounded', SEVERITY_STYLE[item.severity] ?? '')}>
              {item.severity}
            </span>
          )}
          <span className={cn('text-[9px] font-semibold px-1.5 py-0.5 rounded text-white shrink-0', agentFor(item).color)}>
            {agentName(item)}
          </span>
          <span className="text-[12px] text-neutral-700 truncate">{item.summary}</span>
          {hasDetail && (
            <span className="text-[10px] text-neutral-400 shrink-0 flex items-center gap-0.5">
              {expanded ? <ChevronDown className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
            </span>
          )}
        </div>
      </div>
      {expanded && item.detail && (
        <div className="px-5 pb-3">
          <MarkdownContent content={item.detail} className="text-[12px]" />
        </div>
      )}
    </div>
  )
}

function DaySeparator({ date }: { date: string }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <div className="text-[11px] font-semibold text-neutral-400 uppercase tracking-wider">{date}</div>
      <div className="flex-1 h-px bg-neutral-200" />
    </div>
  )
}

type Scope = 'all' | 'issue' | 'experiment'

type FeedEntry = { type: 'single'; item: FeedItem } | { type: 'cluster'; cluster: FeedCluster }

function buildFeedEntries(items: FeedItem[]): FeedEntry[] {
  const entries: FeedEntry[] = []
  const clusterMap = new Map<string, FeedItem[]>()
  const unclustered: FeedItem[] = []

  for (const item of items) {
    if (item.clusterKey) {
      const existing = clusterMap.get(item.clusterKey)
      if (existing) existing.push(item)
      else clusterMap.set(item.clusterKey, [item])
    } else {
      unclustered.push(item)
    }
  }

  for (const [key, clusterItems] of clusterMap) {
    if (clusterItems.length === 1) {
      unclustered.push(clusterItems[0])
    } else {
      const sorted = clusterItems.sort((a, b) => b.timestamp - a.timestamp)
      entries.push({
        type: 'cluster',
        cluster: {
          key,
          scope: sorted[0].scope,
          items: sorted,
          latestTimestamp: sorted[0].timestamp,
        },
      })
    }
  }

  for (const item of unclustered) {
    entries.push({ type: 'single', item })
  }

  entries.sort((a, b) => {
    const tsA = a.type === 'single' ? a.item.timestamp : a.cluster.latestTimestamp
    const tsB = b.type === 'single' ? b.item.timestamp : b.cluster.latestTimestamp
    return tsB - tsA
  })

  return entries
}

export default function AdminFeedPage() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const [scope, setScope] = useState<Scope>('all')

  const feed = useQuery(
    api.admin.getActivityFeed,
    isAdmin ? { scope: scope === 'all' ? undefined : scope, limit: 100 } : 'skip',
  )

  const entries = useMemo(() => feed ? buildFeedEntries(feed) : [], [feed])

  if (!isAdmin) {
    return <div className="p-6 text-sm text-neutral-500">Admin access required</div>
  }

  const groupedByDay: { date: string; entries: FeedEntry[] }[] = []
  if (entries.length > 0) {
    let currentDay = ''
    for (const entry of entries) {
      const ts = entry.type === 'single' ? entry.item.timestamp : entry.cluster.latestTimestamp
      const day = new Date(ts).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
      if (day !== currentDay) {
        currentDay = day
        groupedByDay.push({ date: day, entries: [] })
      }
      groupedByDay[groupedByDay.length - 1].entries.push(entry)
    }
  }

  const issueCt = feed?.filter((i) => i.scope === 'issue').length ?? 0
  const expCt = feed?.filter((i) => i.scope === 'experiment').length ?? 0

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-neutral-900">Feed</h1>
          <p className="text-sm text-neutral-500 mt-0.5">
            What the autonomous agents have been up to
          </p>
        </div>
        <div className="flex items-center gap-1 bg-neutral-100 rounded-lg p-0.5">
          {(['all', 'issue', 'experiment'] as Scope[]).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                scope === s ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500 hover:text-neutral-700',
              )}
            >
              {s === 'all' ? `All${feed ? ` (${feed.length})` : ''}` : s === 'issue' ? `Issues (${issueCt})` : `Experiments (${expCt})`}
            </button>
          ))}
        </div>
      </div>

      {feed === undefined ? (
        <div className="flex items-center justify-center py-20 rounded-lg border border-neutral-200 bg-white">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-300" />
        </div>
      ) : feed.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-neutral-50/50 px-4 py-16 text-center">
          <Newspaper className="h-6 w-6 text-neutral-300 mx-auto mb-2" />
          <div className="text-sm text-neutral-400">No activity yet.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {groupedByDay.map(({ date, entries }) => (
            <div key={date} className="space-y-3">
              <DaySeparator date={date} />
              {entries.map((entry) => {
                if (entry.type === 'cluster') {
                  if (entry.cluster.scope === 'experiment') {
                    return <ExperimentClusterCard key={entry.cluster.key} cluster={entry.cluster} />
                  }
                  return <IssueClusterCard key={entry.cluster.key} cluster={entry.cluster} />
                }
                return <PostCard key={entry.item.id} item={entry.item} />
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
